/**
 * Per-record sync drawer.
 *
 * Открывается из SyncInboxModal по кнопке «Превью» в строке записи.
 * Показывает per-field таблицу: «Сейчас в Jira → Станет (значение из Lens)»
 * для push и «В Jira сейчас → Станет в Lens» для pull, с чекбоксами per
 * field и кнопкой запуска одной записи (через single push/pull endpoint).
 */
import { useEffect, useMemo, useState } from 'react';
import { Drawer, Button, Table, Tag, Typography, Space, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { fetchPushDiff, fetchPullDiff, bulkJiraPush, bulkJiraPull } from './api.ts';
import { useJobs } from '../jobs/JobsContext.tsx';
import type { PushDiffField, PullDiffField, PushDiffResponse, PullDiffResponse } from '@lens/shared';
import { radius } from '../../shared/theme/tokens.ts';

const { Text } = Typography;

interface SyncRecordDrawerProps {
  open: boolean;
  spaceId: string;
  itemId: string;
  direction: 'push' | 'pull';
  /** Глобальный фильтр полей из inbox — здесь применяется как initial. */
  includeFieldSlugs: string[];
  onClose: () => void;
  onApplied: () => void;
}

interface UnifiedField {
  key: string; // unique
  lensFieldSlug: string | null;
  lensFieldName: string | null;
  jiraField: string;
  jiraFieldName: string | null;
  /** Для push: currentJiraValue. Для pull: jiraValue. */
  source: unknown;
  /** Для push: newJiraValue. Для pull: newLensValue. */
  target: unknown;
  /** Для pull: currentLensValue. Для push: undefined. */
  currentLensValue?: unknown;
  willChange: boolean;
  status: 'ok' | 'warning' | 'error';
  message: string | null;
}

export function SyncRecordDrawer({
  open,
  spaceId,
  itemId,
  direction,
  includeFieldSlugs,
  onClose,
  onApplied,
}: SyncRecordDrawerProps) {
  const [diff, setDiff] = useState<PushDiffResponse | PullDiffResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [includedSlugs, setIncludedSlugs] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const { registerJob } = useJobs();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setErrorMsg(null);
    const fetcher = direction === 'push' ? fetchPushDiff : fetchPullDiff;
    fetcher(spaceId, itemId, { includeFieldSlugs })
      .then((res) => {
        if (cancelled) return;
        setDiff(res);
        // По умолчанию включены все поля без error и с willChange
        const initial = new Set<string>();
        for (const f of res.fields) {
          if (f.status !== 'error' && f.willChange) {
            const slug = (f as PushDiffField).lensFieldSlug ?? (f as PullDiffField).lensFieldSlug ?? f.jiraField;
            if (slug) initial.add(slug);
          }
        }
        setIncludedSlugs(initial);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setErrorMsg(err instanceof Error ? err.message : 'Не удалось загрузить diff');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, spaceId, itemId, direction, includeFieldSlugs]);

  const unified: UnifiedField[] = useMemo(() => {
    if (!diff) return [];
    if (direction === 'push') {
      return (diff as PushDiffResponse).fields.map((f, idx) => ({
        key: `${f.jiraField}-${idx}`,
        lensFieldSlug: f.lensFieldSlug,
        lensFieldName: f.lensFieldName,
        jiraField: f.jiraField,
        jiraFieldName: f.jiraFieldName ?? null,
        source: f.currentJiraValue,
        target: f.newJiraValue,
        willChange: f.willChange,
        status: f.status,
        message: f.message,
      }));
    } else {
      return (diff as PullDiffResponse).fields.map((f, idx) => ({
        key: `${f.lensFieldSlug}-${idx}`,
        lensFieldSlug: f.lensFieldSlug,
        lensFieldName: f.lensFieldName,
        jiraField: f.jiraField,
        jiraFieldName: f.jiraFieldName ?? null,
        source: f.jiraValue,
        target: f.newLensValue,
        currentLensValue: f.currentLensValue,
        willChange: f.willChange,
        status: f.status,
        message: f.message,
      }));
    }
  }, [diff, direction]);

  const handleToggle = (slug: string) => {
    setIncludedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleApply = async () => {
    if (includedSlugs.size === 0) {
      void message.warning('Выберите поля для синхронизации');
      return;
    }
    setApplying(true);
    try {
      const slugs = Array.from(includedSlugs);
      // Для обеих сторон используем bulk-job с одним itemId — это даёт
      // консистентный путь: фильтр полей применяется одинаково.
      const res = direction === 'push'
        ? await bulkJiraPush(spaceId, { itemIds: [itemId], includeFieldSlugs: slugs })
        : await bulkJiraPull(spaceId, { itemIds: [itemId], includeFieldSlugs: slugs });
      // Регистрируем в глобальном индикаторе — job продолжится в фоне, даже
      // если пользователь закроет drawer до завершения.
      registerJob(
        res.jobId,
        `${direction === 'push' ? 'Синхронизация в Jira' : 'Импорт из Jira'} · одна запись`,
      );
      await waitJob(res.jobId);
      void message.success(direction === 'push' ? 'Push выполнен' : 'Pull выполнен');
      onApplied();
    } catch (err: unknown) {
      void message.error(err instanceof Error ? err.message : 'Ошибка применения');
    } finally {
      setApplying(false);
    }
  };

  const cols: TableColumnsType<UnifiedField> = [
    {
      title: '',
      key: 'check',
      width: 36,
      render: (_: unknown, row) => (
        <input
          type="checkbox"
          disabled={row.status === 'error' || !row.willChange}
          checked={includedSlugs.has(row.lensFieldSlug ?? row.jiraField)}
          onChange={() => handleToggle(row.lensFieldSlug ?? row.jiraField)}
        />
      ),
    },
    {
      title: 'Поле',
      key: 'field',
      render: (_: unknown, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.lensFieldName ?? '(fixed)'}</div>
          <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
            {row.lensFieldSlug
              ? `${row.lensFieldSlug} → ${row.jiraFieldName ?? row.jiraField}`
              : `→ ${row.jiraFieldName ?? row.jiraField}`}
          </Text>
        </div>
      ),
    },
    {
      title: direction === 'push' ? 'Сейчас в Jira' : 'Сейчас в Lens',
      key: 'source',
      render: (_: unknown, row) => formatValue(direction === 'push' ? row.source : row.currentLensValue),
    },
    {
      title: '→',
      key: 'arrow',
      width: 30,
      align: 'center',
      render: (_: unknown, row) => row.willChange ? <span style={{ color: 'var(--color-info)', fontSize: 16 }}>→</span> : <span style={{ color: 'var(--color-border)' }}>=</span>,
    },
    {
      title: direction === 'push' ? 'Станет (из Lens)' : 'Станет (из Jira)',
      key: 'target',
      render: (_: unknown, row) => (
        <span style={row.willChange ? { background: direction === 'push' ? 'var(--color-info-bg)' : 'var(--color-primary-bg)', padding: '2px 6px', borderRadius: radius.sm } : {}}>
          {formatValue(row.target)}
        </span>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string, row) => {
        const color = s === 'error' ? 'red' : s === 'warning' ? 'orange' : row.willChange ? 'blue' : 'default';
        const label = s === 'error' ? 'ошибка' : s === 'warning' ? 'warning' : row.willChange ? 'изменится' : 'без изменений';
        return (
          <div>
            <Tag color={color}>{label}</Tag>
            {row.message && (
              <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 2 }}>{row.message}</div>
            )}
          </div>
        );
      },
    },
  ];

  const title = diff ? (
    <div>
      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 400 }}>
        {direction === 'push' ? '⇨ Push (Lens → Jira)' : '⇦ Pull (Jira → Lens)'}
        {diff.jiraIssueKey && <span style={{ marginLeft: 8, fontFamily: 'monospace' }}>{diff.jiraIssueKey}</span>}
      </div>
      <div style={{ marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={diff.itemTitle}>
        {diff.itemTitle}
      </div>
    </div>
  ) : 'Загрузка…';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      width={920}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Закрыть</Button>
          <Button
            type="primary"
            loading={applying}
            disabled={includedSlugs.size === 0 || !!errorMsg}
            onClick={() => void handleApply()}
            data-testid="sync-drawer-apply"
          >
            Применить ({includedSlugs.size})
          </Button>
        </div>
      }
    >
      {loading && <div style={{ padding: 40, textAlign: 'center' }}>Загрузка…</div>}
      {errorMsg && <Text type="danger">{errorMsg}</Text>}
      {diff && !loading && !errorMsg && diff.changedFieldCount === 0 && (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Text type="success" style={{ fontSize: 14 }}>✓ Запись уже синхронизирована — ничего обновлять не нужно.</Text>
        </div>
      )}
      {diff && !loading && !errorMsg && diff.changedFieldCount > 0 && (
        <>
          <Space size="large" style={{ marginBottom: 12 }}>
            <Text>
              Изменится полей: <b>{diff.changedFieldCount}</b>
            </Text>
            {diff.warnings > 0 && (
              <Text style={{ color: 'var(--color-warning)' }}>Warnings: {diff.warnings}</Text>
            )}
            {diff.errors > 0 && (
              <Text type="danger">Errors: {diff.errors}</Text>
            )}
          </Space>
          <Table<UnifiedField>
            rowKey="key"
            dataSource={unified}
            columns={cols}
            size="small"
            pagination={false}
          />
        </>
      )}
    </Drawer>
  );
}

function formatValue(v: unknown): React.ReactNode {
  if (v == null) return <Text type="secondary" italic style={{ fontSize: 12 }}>(пусто)</Text>;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toISOString().split('T')[0] ?? v.toISOString();
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join(', ');
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.value === 'string') return o.value;
    if (typeof o.name === 'string') return o.name;
    return <Text code style={{ fontSize: 11 }}>{JSON.stringify(v).slice(0, 60)}</Text>;
  }
  return String(v);
}

async function waitJob(jobId: string): Promise<void> {
  const { getJob } = await import('./api.ts');
  for (let i = 0; i < 60; i++) {
    const j = await getJob(jobId);
    if (j.status === 'success' || j.status === 'error' || j.status === 'cancelled') {
      if (j.status === 'error') throw new Error(j.error ?? 'job failed');
      return;
    }
    await new Promise((r) => setTimeout(r, 1000));
  }
  throw new Error('Timeout ожидания job');
}
